import { AutoAwesome, CopyAll, Close as CloseIcon } from "@mui/icons-material";
import BusinessIcon from "@mui/icons-material/Business";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import LoadingButton from "../../components/LoadingButton";
import { hostname, parseError } from "../../utils";

const CreateListDialog = ({
  open,
  onClose,
  getToken,
  companyId,
  eventListener,
}: {
  open: boolean;
  onClose: Function;
  getToken: Function;
  companyId: string;
  eventListener: Function;
}) => {
  const [listName, setListName] = useState("");
  const [loading, setLoading] = useState(false);
  const [listType, setListType] = useState("leads");
  const [templateId, setTemplateId] = useState<string>("");
  const [templates, setTemplates] = useState([]);

  const loadTemplates = async () => {
    const currentToken = await getToken();
    const res = await axios.get(`${hostname}/lists/templates`, {
      params: { companyId },
      headers: { Authorization: `Bearer ${currentToken}` },
    });
    setTemplates(res.data.templates);
  };

  useEffect(() => {
    if (open) {
      loadTemplates();
    }
  }, [open]);

  const onCreate = async () => {
    if ((!listType && !templateId) || !listName) {
      return;
    }
    
    setLoading(true);
    
    try {
      const currentToken = await getToken();
      var listDict;
      
      if (templateId) {
        listDict = {
          name: listName,
          templateId: templateId,
        };
      } else {
        // Fetch default fields from backend
        const defaultFieldsResponse = await axios.get(
          `${hostname}/lists/default-fields/${listType}`
        );
        
        // Use fields_array to ensure proper ordering, fallback to default_fields for compatibility
        const fieldsData = defaultFieldsResponse.data.fields_array || defaultFieldsResponse.data.default_fields;

        let orderedFields: any = {};
        if (Array.isArray(fieldsData)) {
          // New format: sort by order and rebuild object
          fieldsData
            .sort((a: any, b: any) => a.order - b.order)
            .forEach((field: any) => {
              orderedFields[field.name] = {
                name: field.name,
                label: field.label,
                datatype: field.datatype
              };
            });
        } else {
          // Fallback: use original format
          orderedFields = fieldsData;
        }
        
        listDict = {
          name: listName,
          type: listType,
          fields: orderedFields,
        };
      }
      
      await axios.post(
        `${hostname}/lists`,
        {
          companyId: companyId,
          listDict: listDict,
        },
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      );
      
      setListName("");
      setListType("");
      onClose();
      
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose()}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Create Smart list
        <IconButton onClick={() => onClose()} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Template</InputLabel>
          <Select
            value={templateId}
            label="Template"
            displayEmpty
            startAdornment={<CopyAll sx={{ mr: 2 }} />}
            onChange={(e) => setTemplateId(e.target.value)}
          >
            <MenuItem value="">No Template</MenuItem>
            {templates.map((template: any) => (
              <MenuItem key={template.id} value={template.id}>
                {template.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" align="center" sx={{ margin: "16px 0" }}>
          or
        </Typography>
        <Typography variant="subtitle1">Start from scratch</Typography>
        <Typography variant="body2" gutterBottom>
          Choose the type of list you want to create
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Button
              disabled={templateId !== ""}
              onClick={() => setListType("leads")}
              variant={listType === "leads" ? "contained" : "outlined"}
              fullWidth
              startIcon={<PeopleAltIcon />}
            >
              Leads
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              disabled={templateId !== ""}
              onClick={() => setListType("companies")}
              variant={listType === "companies" ? "contained" : "outlined"}
              fullWidth
              startIcon={<BusinessIcon />}
            >
              Companies
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Button
              disabled={templateId !== ""}
              onClick={() => setListType("custom")}
              variant={listType === "custom" ? "contained" : "outlined"}
              fullWidth
              startIcon={<AutoAwesome />}
            >
              Custom Smart List
            </Button>
          </Grid>
        </Grid>
        <TextField
          fullWidth
          margin="normal"
          label="List name"
          placeholder="e.g. 'SaaS Li Profile Visits'"
          value={listName}
          onChange={(e) => setListName(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()}>Close</Button>
        <LoadingButton
          loading={loading}
          variant="outlined"
          onClick={() => onCreate()}
        >
          Create
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default CreateListDialog;
